import { FC, memo, ReactEventHandler, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { SvgIcon, Tooltip } from '@/base-components';
import { useThemeToken } from '@/hooks';
import type { RootState } from '@@store/index';
import type { SvgIconPropsType, TooltipPropsType } from '@/base-components';
import './index.less';

interface IconBtnPropsType extends Omit<SvgIconPropsType, 'name' | 'className' | 'width' | 'height' | 'onClick' | 'onHover' | 'onLeave' | 'aria-label'> {
    hoverTip?: React.ReactNode | (() => React.ReactNode);
    iconName?: SvgIconPropsType['name'];
    iconColor?: string;
    iconWidth?: SvgIconPropsType['width'],
    iconHeight?: SvgIconPropsType['height'],
    tipPlacement?: TooltipPropsType['placement'];
    tipColor?: TooltipPropsType['color'];
    tipClassName?: string;
    btnClassName?: string;
    children?: React.ReactNode;
    type?: 'text' | 'icon';
    trigger?: TooltipPropsType['trigger'];
    getPopupContainer?: any;
    onClick?: ReactEventHandler<HTMLButtonElement>;
    tipArrow?: TooltipPropsType['arrow'];
    allowDisabledTooltip?: boolean;
    onHover?: ReactEventHandler<HTMLButtonElement>;
    onLeave?: ReactEventHandler<HTMLButtonElement>;
    'aria-label'?: string;
}

const TipButton: FC<IconBtnPropsType> = memo((props) => {
    const curTheme = useSelector((state: RootState) => state.theme);

    const { colorText } = useThemeToken();

    const {
        disabled,
        iconName,
        hoverTip = '提示',
        tipColor,
        iconColor = colorText,
        btnClassName = '',
        tipPlacement = 'left',
        tipClassName = '',
        tipArrow = true,
        type,
        iconWidth,
        iconHeight,
        trigger,
        children,
        getPopupContainer,
        onClick,
        onLeave,
        onHover,
        allowDisabledTooltip = false,
        'aria-label': ariaLabel,
        ...rest
    } = props;

    const [ isTipOpen, setIsTipOpen ] = useState(false);
    const canShowTooltip = allowDisabledTooltip || !disabled;

    const btnTypeClassName = useMemo(() => {
        switch (type) {
            case 'icon':
                return `base-icon-btn ${curTheme}-base-icon-btn`;
            case 'text':
                return `base-text-btn ${curTheme}-base-text-btn`;
            default:
                return '';
        }
    }, [ type, curTheme ]);

    const onMouseLeave = (e) => {
        setIsTipOpen(false);
        onLeave && onLeave(e);
    };

    const onBtnClick = (e) => {
        e.stopPropagation();
        onClick && onClick(e);
    };

    return (
        <Tooltip
            classNames={{
                root: `${tipClassName} sidebar-tooltip`
            }}
            title={hoverTip}
            placement={tipPlacement}
            trigger={trigger}
            color={tipColor}
            open={isTipOpen && canShowTooltip}
            onOpenChange={open => setIsTipOpen(open)}
            getPopupContainer={getPopupContainer}
            mouseEnterDelay={0.1}
            mouseLeaveDelay={0.1}
            arrow={tipArrow}
        >
            <button
                type="button"
                onMouseLeave={onMouseLeave}
                onMouseEnter={onHover}
                onClick={onBtnClick}
                disabled={disabled}
                aria-label={ariaLabel}
                className={btnClassName}
            >
                <SvgIcon
                    disabled={disabled}
                    name={iconName}
                    color={iconColor}
                    width={iconWidth}
                    height={iconHeight}
                    className={`${btnTypeClassName} ${btnClassName}`}
                    {...rest}
                >
                    {children}
                </SvgIcon>
            </button>
        </Tooltip>
    );
});

export default TipButton;
